import moment, { Moment } from 'moment';
import { normalizeToFullYear } from './formatting';
import { IDate, IPhone } from '../types';
import { PhoneNumber as GPhoneNumber, PhoneNumberUtil } from 'google-libphonenumber';
import { ZodSchema, ZodFormattedError } from 'zod';
import { ZodValidationResult } from 'types';

export const isOverXDaysAgo = (userDate?: string, days?: number) => {
  if (userDate) {
    const date = moment(userDate, 'YYYY-MM-DD', true);
    return date.startOf('day').isSameOrBefore(moment().startOf('day').subtract(days, 'days'));
  }
  return false;
};

export const isOverXMonthsAgo = (userDate: string, months: number) => {
  const date = moment(userDate, 'YYYY-MM-DD', true);
  return date.isSameOrBefore(moment().subtract(months, 'months'));
};

export const getMaturityDate = (userDate?: string) => {
  if (userDate) {
    const date = moment(userDate, 'YYYY-MM-DD', true);
    return date.startOf('day').add(28, 'days'); //.format('DD/MM/YYYY');
  } else {
    return moment().startOf('day').add(28, 'days');
  }
};

const currentYear = new Date().getFullYear();
const MIN_YEAR = currentYear - 130;
const MAX_YEAR = currentYear + 50;

export const getMomentDate = (dateInput: IDate): Moment => {
  const { YYYY, DD, MM } = dateInput;
  const year = normalizeToFullYear(YYYY);
  return moment([year, Number(MM) - 1, Number(DD)]);
};

export const getMomentDateNoDays = (dateInput: IDate): Moment => {
  const { YYYY, MM } = dateInput;
  const year = normalizeToFullYear(YYYY);
  return moment([year, Number(MM) - 1]);
};

export const getMomentDateOrNull = (dateInput: IDate): Moment | null => {
  const { YYYY, DD, MM } = dateInput;
  if (YYYY && DD && MM) {
    return getMomentDate(dateInput);
  }
  return null;
};

export const getMomentDateOrNullNoDays = (dateInput: IDate): Moment | null => {
  const { YYYY, MM } = dateInput;
  if (YYYY && MM) {
    return getMomentDateNoDays(dateInput);
  }
  return null;
};

export const isValidDate = (dateInput: IDate): boolean => {
  const momentDate = getMomentDateOrNull(dateInput);
  if (momentDate) {
    const year = momentDate.year();
    if (year && (year < MIN_YEAR || year > MAX_YEAR)) {
      return false;
    }
    return momentDate.isValid();
  }
  return false;
};

export const isValidDateNoDays = (dateInput: IDate): boolean => {
  const momentDate = getMomentDateOrNullNoDays(dateInput);
  if (momentDate) {
    const year = momentDate.year();
    if (year && (year < MIN_YEAR || year > MAX_YEAR)) {
      return false;
    }
    return momentDate.isValid();
  }
  return false;
};

export const isFutureDate = (dateInput: IDate): boolean => {
  const futureDate = getMomentDateOrNull(dateInput);
  if (futureDate) return futureDate.isSameOrAfter(moment());
  return false;
};

export const isPastDate = (dateInput: IDate): boolean => {
  const pastDate = getMomentDateOrNull(dateInput);
  if (pastDate) return pastDate.isSameOrBefore(moment());
  return false;
};

export const isValidDateAndPast = (dateInput: IDate): boolean => {
  const pastDate = getMomentDateOrNull(dateInput);
  if (pastDate) {
    return pastDate.isSameOrBefore(moment());
  }
  return false;
};

export const getPhoneNumber = (code: string, phone: string): GPhoneNumber => {
  const util = PhoneNumberUtil.getInstance();
  let phoneNum = new GPhoneNumber();
  try {
    const rawNumber = `${code}${phone}`.replace(/[^0-9]/g, '');
    phoneNum = util.parseAndKeepRawInput(`+${rawNumber}`);
  } catch {
    phoneNum.setCountryCode(Number(code));
    phoneNum.setNationalNumber(Number(phone));
  }
  return phoneNum;
};

export const isCaribbeanFormatPossiblePhoneNumberValid = (code: string, phone: string): boolean => {
  const regexCode = RegExp(/^[0-9]{4}$/);
  const regexPhone = RegExp(/^[0-9]{7}$/);
  return regexCode.test(code) && regexPhone.test(phone);
};

export const isCountryCodeValid = (code: string, phone: string): boolean => {
  const util = PhoneNumberUtil.getInstance();
  try {
    // entered PhoneNumber
    const countryCodeInput = Number(code);
    const nationalNumberInput = Number(phone);
    const phoneNum = new GPhoneNumber();
    phoneNum.setCountryCode(countryCodeInput);
    phoneNum.setNationalNumber(nationalNumberInput);
    const regionCodeInput = util.getRegionCodeForNumber(phoneNum);

    // parsed PhoneNumber
    const rawNumber = `${code}${phone}`.replace(/[^0-9]/g, '');
    const phoneNumParsed = util.parseAndKeepRawInput(`+${rawNumber}`);
    const regionCodeParsed = util.getRegionCodeForNumber(phoneNumParsed);
    const countryCodeParsed = phoneNumParsed.getCountryCode();

    if (countryCodeInput === countryCodeParsed) return true; // parsed and entered country codes are same
    if (countryCodeInput === 1) return true; // US
    if (countryCodeParsed === 1 && !regionCodeParsed) return false; // Caribbean island (or US region) is parsed, but region code is not parsed
    if (countryCodeParsed !== 1 && !regionCodeInput) return false; // another country is parsed, but entered invalid country code

    return true;
  } catch {
    // skip this validation
    return true;
  }
};

class IValidateZodSchemaOpts {
  inArrayFormat: boolean; // Serialize this error into string array
}

export function validateZodSchema<T>(
  schema: ZodSchema<T>,
  obj: unknown,
  opts: IValidateZodSchemaOpts = { inArrayFormat: false }
): ZodValidationResult<T> {
  const result = schema.safeParse(obj);

  if (result.success) {
    return {
      success: true,
      data: result.data
    };
  } else {
    const formattedErrors: ZodFormattedError<T> = result.error.format();

    if (opts.inArrayFormat === true) {
      const errors: string[] = [];
      for (const [field, error] of Object.entries(formattedErrors)) {
        if (field !== '_errors' && typeof error === 'object' && '_errors' in error) {
          errors.push(...error._errors.map(err => `${field}: ${err}`));
        }
      }
      return { success: false, errors };
    }

    const errors: Record<string, string[]> = {};
    for (const [field, error] of Object.entries(formattedErrors)) {
      if (field !== '_errors' && typeof error === 'object' && '_errors' in error) {
        errors[field] = error._errors;
      }
    }
    return {
      success: false,
      errors
    };
  }
}

export const getCodeAndPhoneNo = (phone?: IPhone | string): IPhone => {
  if (typeof phone === 'string' && phone.split('-')?.length === 2) {
    const [code, phoneNo] = phone.split('-');
    return { code, phone: phoneNo };
  } else if (phone && typeof phone === 'object') {
    return {
      code: phone.code,
      phone: phone.phone
    };
  }
  return {
    code: '',
    phone: ''
  };
};
